import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Spin,
  Modal,
  theme,
  App,
  type MenuProps,
} from 'antd';
import {
  DollarOutlined,
  BankOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';

import { useVendorDetail, useVendorMutations, useVendorLocations, useVendorLocationDetail, useLocationMutations } from '@/hooks/useVendors';
import { useContacts } from '@/hooks/useContacts';
import { usePayment, usePaymentMutations } from '@/hooks/usePayment';
import { useBankAccounts, useBankAccountMutations } from '@/hooks/useBanks';
import { useTransactions } from '@/hooks/useTransactions';
import { useLocationPreload } from '@/hooks/useLocations';
import type { BankAccountResponse, ContactResponse, VendorLocationResponse } from '@/types/api';
import type { BankAccountFormInput } from '@/types/vendors/bankAccount';
import BankAccountFormModal from '../BankAccountFormModal';
import LocationFormModal, { type LocationFormData } from '../LocationFormModal';
import EditBasicInfoModal from '../EditBasicInfoModal';
import EditFinancialInfoModal from '../EditFinancialInfoModal';
import EditVendorHeaderModal from '../EditVendorHeaderModal';
import { getCategories } from '@/apis/endpoints/categories';
import type { CategoryListItem } from '@/types/api/category';
import { controlHeightTokens } from '@/styles/tokens';
import { ConfirmationDialog, useConfirmationDialog } from '@/components/common/ConfirmationDialog';
import type { VendorDetailResponse } from '@/types/api';
import { DEFAULT_COUNTRY_CODE } from '@/utils/phoneUtils';
import { sanitizeLocationIds } from '@/utils/locationUtils';
import { parseEntityRouteId } from '@/utils/routeIdUtils';
import { useCascadingLocations } from '@/hooks/useLocations';
import { useVendorPermissions } from '@/hooks/useVendorPermissions';

// Import extracted components
import { VendorHeader } from './VendorHeader';
import { VendorTabs } from './VendorTabs';
import PrimaryInfoSection from './sections/PrimaryInfoSection';
import ContactsSection from './sections/ContactsSection';
import LocationsSection from './sections/LocationsSection';
import { PaymentSection } from './sections/PaymentSection';
import BankAccountsSection from './sections/BankAccountsSection';
import {
  TransactionsSection,
  type TransactionFilterState,
} from './sections/TransactionsSection';
import PageContainer from '@/components/common/PageContainer';

const VendorDetailPage = () => {
  const { t, i18n } = useTranslation('vendors');
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { message: messageApi, modal } = App.useApp();
  const [activeSection, setActiveSection] = useState('primary');
  const [bankAccountModalOpen, setBankAccountModalOpen] = useState(false);
  const [editingBankAccount, setEditingBankAccount] = useState<BankAccountFormInput | null>(null);
  const [editingBankId, setEditingBankId] = useState<number | null>(null);
  const [bankAccountReadOnly, setBankAccountReadOnly] = useState(false);
  // #91 — no contact modal state: the vendor detail contact section is
  // view-only; contact create/edit lives in the Contacts module.
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [editingLocationId, setEditingLocationId] = useState<number | null>(null);
  const [locationReadOnly, setLocationReadOnly] = useState(false);
  const [includeInactiveContacts, setIncludeInactiveContacts] = useState(false);
  const [includeInactiveLocations, setIncludeInactiveLocations] = useState(false);
  const [includeInactiveBankAccounts, setIncludeInactiveBankAccounts] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [togglingBank, setTogglingBank] = useState(false);
  const [togglingLocation, setTogglingLocation] = useState(false);
  const [settingDefaultLocation, setSettingDefaultLocation] = useState(false);
  const [settingDefaultBank, setSettingDefaultBank] = useState(false);

  // Edit modal states
  const [editBasicInfoModalOpen, setEditBasicInfoModalOpen] = useState(false);
  const [editFinancialInfoModalOpen, setEditFinancialInfoModalOpen] = useState(false);
  const [editHeaderModalOpen, setEditHeaderModalOpen] = useState(false);

  // Confirmation dialog for activate/deactivate vendor
  const toggleStatusDialog = useConfirmationDialog<VendorDetailResponse>();

  // Confirmation dialog for activate/deactivate bank account
  const toggleBankStatusDialog = useConfirmationDialog<BankAccountResponse>();

  // Confirmation dialog for activate/deactivate location
  const toggleLocationStatusDialog = useConfirmationDialog<VendorLocationResponse>();

  // Confirmation dialog for set default location
  const setDefaultLocationDialog = useConfirmationDialog<VendorLocationResponse>();

  // Confirmation dialog for set default bank
  const setDefaultBankDialog = useConfirmationDialog<BankAccountResponse>();

  // Convert vendorId to number — tolerant of the real BE's "TRN-0001"
  // string list ids (parseInt would yield NaN and suppress the request)
  const numericVendorId = parseEntityRouteId(vendorId);

  // Fetch vendor details
  // Note: isLoading will be true until data arrives (handles cache race conditions)
  const { vendor, isLoading: loading, error: _vendorError, refresh } = useVendorDetail(numericVendorId);

  // Vendor permissions - controls edit/add actions based on vendor status
  const { canEdit, canAdd, getItemPermissions } = useVendorPermissions(vendor);

  // Fetch contacts (sorting is now handled internally by ContactsSection)
  const contactParams = useMemo(() => ({
    pageSize: -1, // Get all contacts
  }), []);
  const { contacts, isLoading: contactsLoading } = useContacts(numericVendorId, contactParams);

  // Fetch vendor locations (sorting is now handled internally by LocationsSection)
  const locationParams = useMemo(() => ({
    pageSize: -1, // Get all locations
  }), []);
  const { locations, isLoading: locationsLoading, refresh: refreshLocations } = useVendorLocations(numericVendorId, locationParams);

  // Fetch single location detail for edit modal (only when editingLocationId is set)
  const { location: locationDetail, isLoading: locationDetailLoading } = useVendorLocationDetail(
    numericVendorId,
    editingLocationId
  );

  // Transform locationDetail to LocationFormData for the edit modal
  const editingLocationFromApi = useMemo((): LocationFormData | null => {
    if (!locationDetail) return null;
    return {
      locationName: locationDetail.locationName,
      isDefaultReceiving: locationDetail.isDefaultReceiving,
      country: locationDetail.country,
      countryId: locationDetail.countryId ?? null,
      stateId: locationDetail.stateId ?? null,
      state: locationDetail.state ?? '',
      cityId: locationDetail.cityId ?? null,
      city: locationDetail.city ?? '',
      postcode: locationDetail.postcode ?? '',
      address: locationDetail.address ?? '',
      addressLang: locationDetail.addressLang ?? null,
      notes: locationDetail.notes ?? '',
    };
  }, [locationDetail]);


  // Fetch payment data
  const { paymentData: payment, refresh: refreshPayment } = usePayment(numericVendorId);

  // Payment mutations (for cache invalidation on update)
  const { updatePaymentData } = usePaymentMutations(numericVendorId ?? 0);

  // Fetch bank accounts
  const { bankAccounts, isLoading: banksLoading, refresh: refreshBankAccounts } = useBankAccounts(numericVendorId);

  // Fetch transactions — filters drive the API query params (server-side
  // filtering, #66/#87), not client-side slicing
  const [transactionFilters, setTransactionFilters] = useState<TransactionFilterState>({});
  const { transactions, isLoading: transactionsLoading } = useTransactions(
    numericVendorId,
    transactionFilters
  );

  // Categories state for header edit modal (local state like wizard pattern)
  const [categories, setCategories] = useState<CategoryListItem[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Fetch categories from API (reusable function for refresh - same pattern as wizard)
  const fetchCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      const response = await getCategories({ isActive: 1, pageSize: -1 });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // State for resolved country code (needed for cascading locations)
  const [vendorCountryCode, setVendorCountryCode] = useState<string | null>(null);

  // Get cascading location data to resolve country/state/city names
  // Use vendor's addressLang to display location names in the language the address was entered in
  const { countries, states, cities } = useCascadingLocations(
    vendorCountryCode,
    vendor?.stateId ?? null,
    vendor?.addressLang // Display location names based on the address language (en/zh)
  );

  // Resolve country code when vendor and countries load
  useEffect(() => {
    if (vendor?.countryId && countries.length > 0) {
      const country = countries.find((c) => c.countryId === vendor.countryId);
      if (country && country.countryCode !== vendorCountryCode) {
        setVendorCountryCode(country.countryCode);
      }
    }
  }, [vendor?.countryId, countries, vendorCountryCode]);

  // Preload location data for display
  useLocationPreload();

  // Vendor mutations
  const { toggleStatus, updateVendor } = useVendorMutations();

  // Bank account mutations - always call hook unconditionally
  const bankMutations = useBankAccountMutations(numericVendorId!);
  const toggleBankStatus = numericVendorId ? bankMutations.toggleStatus : async () => {};
  const setPrimaryBank = numericVendorId ? bankMutations.setDefault : async () => {};
  const createBankAccountMutation = numericVendorId
    ? bankMutations.createBankAccount
    : async () => {};
  const updateBankAccountMutation = numericVendorId
    ? bankMutations.updateBankAccount
    : async () => {};

  // Location mutations - always call hook unconditionally
  const locationMutations = useLocationMutations(numericVendorId!);

  // Design settings (from refined version)
  const currentColorScheme = {
    headerBg: token.colorBgContainer,
    headerAccent: token.colorPrimary,
    cardShadow: token.boxShadow,
    borderColor: token.colorBorderSecondary,
    highlightBg: token.colorPrimaryBg,
  };

  const currentFontSizes = {
    title: token.fontSizeHeading2,
    subtitle: token.fontSize,
    body: token.fontSize,
    label: token.fontSizeSM,
    menuItem: token.fontSizeLG,
  };

  const handleAddBankAccount = () => {
    setEditingBankAccount(null);
    setEditingBankId(null);
    setBankAccountReadOnly(false);
    setBankAccountModalOpen(true);
  };

  const handleEditBankAccount = (account: BankAccountResponse) => {
    // Convert API response to form input format
    // API now returns bankCode and branchCode as separate fields
    const formData: BankAccountFormInput = {
      isDefault: account.isDefault,
      accountName: account.accountName,
      countryId: account.countryId,
      bankLocation: account.countryNameEn ?? '', // Use country name for display
      bankCode: account.bankCode ?? '',
      bankName: account.bankName,
      currency: account.currency,
      accountNumber: account.accountNumber,
      branchCode: account.branchCode ?? '',
      branchName: account.branchName ?? '',
      swiftCode: account.swiftCode ?? '',
      iban: account.iban ?? '',
      notes: account.notes ?? '',
      isActive: account.isActive,
    };
    setEditingBankAccount(formData);
    setEditingBankId(account.bankId);
    setBankAccountReadOnly(false);
    setBankAccountModalOpen(true);
  };

  const handleViewBankAccount = (account: BankAccountResponse) => {
    // Open bank account modal in read-only mode
    const formData: BankAccountFormInput = {
      isDefault: account.isDefault,
      accountName: account.accountName,
      countryId: account.countryId,
      bankLocation: account.countryNameEn ?? '',
      bankCode: account.bankCode ?? '',
      bankName: account.bankName,
      currency: account.currency,
      accountNumber: account.accountNumber,
      branchCode: account.branchCode ?? '',
      branchName: account.branchName ?? '',
      swiftCode: account.swiftCode ?? '',
      iban: account.iban ?? '',
      notes: account.notes ?? '',
      isActive: account.isActive,
    };
    setEditingBankAccount(formData);
    setEditingBankId(account.bankId);
    setBankAccountReadOnly(true);
    setBankAccountModalOpen(true);
  };

  const _handleDeleteBankAccount = async (accountId: number) => {
    if (!numericVendorId) return;

    Modal.confirm({
      title: t('vendor.actions.delete'),
      content: t('vendor.messages.confirmDeleteAccount'),
      okText: t('vendor.actions.delete'),
      okType: 'danger',
      cancelText: t('vendor.actions.cancel'),
      onOk: async () => {
        try {
          await toggleBankStatus(accountId, false);
          messageApi.success(t('vendor.messages.accountDeletedSuccess'));
          // Refresh bank accounts list (not vendor details)
          refreshBankAccounts();
        } catch (error: unknown) {
          const errorMessage =
            (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
            t('vendor.messages.deleteFailed');
          messageApi.error(errorMessage);
          console.error('Delete bank account error:', error);
        }
      },
    });
  };

  const handleSetDefaultBankClick = async (bankAccount: BankAccountResponse) => {
    if (!numericVendorId) return;
    // Open confirmation dialog
    setDefaultBankDialog.open(bankAccount);
  };

  // #91: contact handlers (add/edit/view-modal/set-primary/toggle) were removed —
  // the section is view-only and rows navigate to the Contacts module.

  const handleOpenContactInModule = (contact: ContactResponse) => {
    // The org-scoped contactId IS the master-data contactId (mock seed keeps the
    // contactId sets identical); entityId + type give the detail page its scope.
    navigate(`/auth/contacts/${contact.contactId}?entityId=${numericVendorId}&type=1`);
  };

  const handleSaveLocation = async (locationData: LocationFormData): Promise<void> => {
    if (!numericVendorId) return;

    // Add addressLang and sanitize location IDs for API compatibility
    const dataForApi = {
      locationName: locationData.locationName,
      isDefaultReceiving: locationData.isDefaultReceiving,
      ...sanitizeLocationIds({ countryId: locationData.countryId, stateId: locationData.stateId, cityId: locationData.cityId }),
      postcode: locationData.postcode,
      address: locationData.address,
      addressLang: i18n.language.startsWith('zh') ? 'zh' : 'en',
      notes: locationData.notes,
    };

    try {
      if (editingLocationId) {
        // Update existing location
        await locationMutations.updateLocation(editingLocationId, dataForApi);
        messageApi.success(t('vendor.notifications.locationUpdated'));
      } else {
        // Create new location
        await locationMutations.createLocation(dataForApi);
        messageApi.success(t('vendor.notifications.locationAdded'));
      }
      // Refresh locations list (cache already invalidated inside mutation)
      await refreshLocations();
      setLocationModalOpen(false);
      setEditingLocationId(null);
    } catch (error: unknown) {
      const errorMessage =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        t('vendor.notifications.locationSaveFailed');
      messageApi.error(errorMessage);
      console.error('Save location error:', error);
      throw error; // Re-throw so modal knows save failed
    }
  };

  const handleSaveBankAccount = async (bankAccountData: BankAccountFormInput): Promise<void> => {
    if (!numericVendorId) return;

    // Check if setting isDefault to true and there's already a default bank account
    const currentDefault = bankAccounts?.find((b) => b.isDefault);
    const isSettingNewDefault =
      bankAccountData.isDefault &&
      currentDefault &&
      currentDefault.bankId !== editingBankId;

    // Helper function to perform the actual save
    const performSave = async (unsetCurrentDefault?: BankAccountResponse) => {
      // If we need to unset current default first
      if (unsetCurrentDefault) {
        // Use setDefault API to unset the current default
        await setPrimaryBank(unsetCurrentDefault.bankId, false);
      }

      if (editingBankId) {
        // Update existing bank account
        await updateBankAccountMutation(editingBankId, bankAccountData);
        // If setting as default, call set-default API separately
        // (update API may not handle isDefault flag)
        if (bankAccountData.isDefault) {
          await setPrimaryBank(editingBankId, true);
        }
        messageApi.success(t('vendor.notifications.bankAccountUpdated'));
      } else {
        // Create new bank account
        const newBank = await createBankAccountMutation(bankAccountData);
        // If setting as default, call set-default API separately
        if (bankAccountData.isDefault && newBank?.bankId) {
          await setPrimaryBank(newBank.bankId, true);
        }
        messageApi.success(t('vendor.notifications.bankAccountAdded'));
      }
      setBankAccountModalOpen(false);
      setEditingBankAccount(null);
      setEditingBankId(null);
      // Store is updated optimistically by mutations, no need to refresh
    };

    // If setting new default, show confirmation modal
    if (isSettingNewDefault && currentDefault) {
      return new Promise<void>((resolve, reject) => {
        modal.confirm({
          title: t('vendor.modals.defaultBankAccountExists'),
          icon: <InfoCircleOutlined style={{ color: token.colorPrimary }} />,
          content: t('vendor.modals.replaceDefaultBankAccount', {
            current: currentDefault.accountName,
            new: bankAccountData.accountName,
          }),
          okText: t('vendor.modals.replaceDefault'),
          okType: 'primary',
          cancelText: t('vendor.actions.cancel'),
          okButtonProps: {
            style: { height: `${controlHeightTokens.controlHeight}px` },
          },
          cancelButtonProps: {
            style: { height: `${controlHeightTokens.controlHeight}px` },
          },
          onOk: async () => {
            try {
              await performSave(currentDefault);
              resolve();
            } catch (error) {
              reject(error);
            }
          },
          onCancel: () => {
            // User cancelled, resolve without error (just stop loading)
            resolve();
          },
        });
      });
    } else {
      // No default conflict, save directly
      try {
        await performSave();
      } catch (error: unknown) {
        const errorMessage =
          (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
          t('vendor.messages.updateFailed');
        messageApi.error(errorMessage);
        console.error('Save bank account error:', error);
        throw error; // Re-throw so modal knows save failed
      }
    }
  };

  // Edit basic info handler
  const handleSaveBasicInfo = async (data: {
    phoneCountryCode: string;
    phoneNumber: string;
    phoneExtension?: string;
    mobile?: string;
    email: string;
    web?: string;
    fax?: string;
    stateId: number | null;
    stateName: string;
    cityId: number | null;
    cityName: string;
    postcode?: string;
    address?: string;
    salesRepresentativeIds?: number[];
    primaryPort?: string;
  }) => {
    if (!numericVendorId || !vendor) return;

    try {
      // Call vendor update API with merged data
      await updateVendor(numericVendorId, {
        name: vendor.name,
        alternativeName: vendor.alternativeName || '',
        nickname: vendor.nickname || '',
        vendorCode: vendor.vendorCode,
        localTaxId: vendor.localTaxId || '',
        individual: vendor.individual ?? false,
        categoryIds: vendor.categories?.map((c) => c.categoryId) || [],
        ...sanitizeLocationIds({
          countryId: vendor.countryId,
          stateId: data.stateId || vendor.stateId,
          cityId: data.cityId || vendor.cityId,
        }),
        postcode: data.postcode,
        address: data.address,
        addressLang: i18n.language.startsWith('zh') ? 'zh' : 'en', // Address language based on current UI language
        phoneCountryCode: data.phoneCountryCode,
        phoneNumber: data.phoneNumber,
        phoneExtension: data.phoneExtension || undefined,
        email: data.email,
        web: data.web,
        fax: data.fax,
        salesRepresentativeIds: data.salesRepresentativeIds,
        primaryPort: data.primaryPort,
      });
      // Refresh to get updated data (cache-aware, won't show full-page loading spinner)
      refresh();
    } catch (error: unknown) {
      console.error('Save basic info error:', error);
      throw error;
    }
  };

  // Edit financial info handler
  const handleSaveFinancialInfo = async (data: {
    deposit?: number;
    cashDiscount?: number;
    cashDiscountRate?: number;
    cashDiscountDays?: number;
    termsTypeId: number;
    termsDays: number;
    primaryCurrency: string;
    abn: string;
    taxTypeId: number;
    taxRate: number;
  }) => {
    if (!numericVendorId) return;

    try {
      // Determine if discount is enabled (from conditional discount rate/days fields)
      const hasDiscount = !!(data.cashDiscountRate && data.cashDiscountRate > 0);

      // Use mutation hook - updates store + invalidates cache
      await updatePaymentData({
        basePaymentTermId: data.termsTypeId,
        primaryCurrency: data.primaryCurrency,
        taxTypeId: data.taxTypeId,
        customDueDays: data.termsDays,
        // Deposit and Cash Discount percentages (always visible fields)
        ...(data.deposit !== undefined && { depositPercentage: data.deposit }),
        ...(data.cashDiscount !== undefined && { cashDiscountPercentage: data.cashDiscount }),
        // Discount fields - all three required when discount is enabled (conditional fields)
        ...(hasDiscount && {
          customHasDiscount: true,
          customDiscountRate: data.cashDiscountRate,
          customDiscountDays: data.cashDiscountDays,
        }),
      });
      // Refresh payment data (cache already invalidated by mutation hook)
      refreshPayment();
    } catch (error: unknown) {
      console.error('Save financial info error:', error);
      throw error;
    }
  };

  // Edit header info handler
  const handleSaveHeader = async (data: {
    name: string;
    alternativeName: string;
    nickname: string;
    type: 'company' | 'individual';
    categoryIds: number[];
    notes: string;
  }) => {
    if (!numericVendorId || !vendor) return;

    try {
      // Call vendor update API with merged data
      // IMPORTANT: Must include all phone fields (not legacy 'phone') and addressLang to preserve data
      await updateVendor(numericVendorId, {
        name: data.name,
        alternativeName: data.alternativeName,
        nickname: data.nickname,
        vendorCode: vendor.vendorCode,
        localTaxId: vendor.localTaxId || '',
        individual: data.type === 'individual',
        categoryIds: data.categoryIds,
        // Address fields
        countryId: vendor.countryId,
        stateId: vendor.stateId,
        cityId: vendor.cityId,
        postcode: vendor.postcode || '',
        address: vendor.address || '',
        addressLang: vendor.addressLang || (i18n.language.startsWith('zh') ? 'zh' : 'en'),
        // Phone fields - use new format, not legacy 'phone' field
        phoneCountryCode: vendor.phoneCountryCode || DEFAULT_COUNTRY_CODE,
        phoneNumber: vendor.phoneNumber || '',
        phoneExtension: vendor.phoneExtension || undefined,
        // Other contact fields
        email: vendor.email || '',
        web: vendor.web || '',
        fax: vendor.fax || '',
        // Preserve other vendor fields
        primaryPort: vendor.primaryPort || undefined,
        salesRepresentativeIds: vendor.salesRepresentativeIds || undefined,
        notes: data.notes,
      });
      // Refresh to get updated data (cache-aware, won't show full-page loading spinner)
      refresh();
    } catch (error: unknown) {
      console.error('Save header info error:', error);
      throw error;
    }
  };

  // Show loading spinner during initial load or when refreshing
  // Note: loading will be true until data arrives (see useVendorDetail hook)
  if (loading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-h-96">
        <Spin size="large" />
      </div>
    );
  }

  // Only show "not found" if loading is complete and vendor doesn't exist
  // This prevents the flash of "not found" before data loads
  if (!vendor) {
    return (
      <PageContainer>
        <div className="tw-text-center tw-py-8">
          <p style={{ color: token.colorTextSecondary }}>{t('vendor.messages.vendorNotFound')}</p>
          <Button
            type="primary"
            onClick={() => navigate('/auth/vendors')}
            style={{
              height: `${controlHeightTokens.controlHeightXL}px`,
              fontSize: currentFontSizes.body,
            }}
          >
            {t('vendor.buttons.back')}
          </Button>
        </div>
      </PageContainer>
    );
  }

  // Menu items for sidebar
  const menuItems: MenuProps['items'] = [
    {
      key: 'primary',
      icon: <FileTextOutlined />,
      label: t('vendor.tabs.primaryInfo'),
    },
    {
      key: 'financial',
      icon: <DollarOutlined />,
      label: t('vendor.tabs.financialInfo'),
    },
    {
      key: 'bank',
      icon: <BankOutlined />,
      label: t('vendor.tabs.bankAccounts'),
    },
    {
      key: 'transaction',
      icon: <FileTextOutlined />,
      label: t('vendor.sections.transactionHistory'),
    },
  ];

  // Render content based on active section
  const renderContent = () => {
    switch (activeSection) {
      case 'primary':
        return (
          <PrimaryInfoSection
            vendor={vendor}
            canEdit={canEdit}
            onEditBasicInfo={() => setEditBasicInfoModalOpen(true)}
            countries={countries}
            states={states}
            cities={cities}
            contactsSection={
              <ContactsSection
                contacts={contacts || []}
                loading={contactsLoading}
                includeInactive={includeInactiveContacts}
                onIncludeInactiveChange={setIncludeInactiveContacts}
                onOpenContact={handleOpenContactInModule}
                onAddInContactsModule={() => navigate('/auth/contacts')}
              />
            }
            locationsSection={
              <LocationsSection
                locations={locations || []}
                loading={locationsLoading}
                includeInactive={includeInactiveLocations}
                onIncludeInactiveChange={setIncludeInactiveLocations}
                canEdit={canEdit}
                canAdd={canAdd}
                getItemPermissions={getItemPermissions}
                onAdd={() => {
                  setEditingLocationId(null);
                  setLocationReadOnly(false);
                  setLocationModalOpen(true);
                }}
                onEdit={(location) => {
                  setEditingLocationId(location.locationId);
                  setLocationReadOnly(false);
                  setLocationModalOpen(true);
                }}
                onRowClick={(location) => {
                  setEditingLocationId(location.locationId);
                  setLocationReadOnly(true);
                  setLocationModalOpen(true);
                }}
                onSetDefault={(location) => setDefaultLocationDialog.open(location)}
                onToggleStatus={(location) => toggleLocationStatusDialog.open(location)}
                currentFontSizes={currentFontSizes}
              />
            }
          />
        );

      case 'financial':
        return (
          <PaymentSection
            payment={payment}
            vendor={vendor}
            canEdit={canEdit}
            onEditClick={() => setEditFinancialInfoModalOpen(true)}
            currentColorScheme={currentColorScheme}
            currentFontSizes={currentFontSizes}
          />
        );

      case 'bank':
        return (
          <BankAccountsSection
            bankAccounts={bankAccounts || []}
            loading={banksLoading}
            includeInactive={includeInactiveBankAccounts}
            onIncludeInactiveChange={setIncludeInactiveBankAccounts}
            canEdit={canEdit}
            canAdd={canAdd}
            getItemPermissions={getItemPermissions}
            onAdd={handleAddBankAccount}
            onView={handleViewBankAccount}
            onEdit={handleEditBankAccount}
            onSetDefault={handleSetDefaultBankClick}
            onToggleStatus={(account) => toggleBankStatusDialog.open(account)}
            addressLang={vendor?.addressLang}
          />
        );

      case 'transaction':
        return (
          <TransactionsSection
            transactions={transactions}
            isLoading={transactionsLoading}
            filters={transactionFilters}
            onFiltersChange={setTransactionFilters}
          />
        );

      default:
        return null;
    }
  };

  return (
    <PageContainer>
      {/* Production Enhanced Table Styling */}
      <style>
        {`
          /* Production Enhanced - Tabular numbers for all tables */
          .production-enhanced .ant-table-cell {
            font-variant-numeric: tabular-nums;
            font-feature-settings: 'tnum';
          }

          /* Consistent 40px row height */
          .production-enhanced .ant-table-tbody > tr > td {
            height: 40px;
            min-height: 40px;
          }

          /* Title case headers (no transformation) */
          .production-enhanced .ant-table-thead > tr > th {
            font-weight: ${token.fontWeightStrong};
          }

          /* Inactive row styling - uniform dimmed appearance */
          /* Must target both tr AND td because Ant Design sets background on cells */
          .production-enhanced .ant-table-tbody > tr.inactive-row,
          .production-enhanced .ant-table-tbody > tr.inactive-row > td {
            background-color: ${token.colorBgLayout} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row:hover > td,
          .production-enhanced .ant-table-tbody > tr.inactive-row:active > td,
          .production-enhanced .ant-table-tbody > tr.inactive-row:focus > td {
            background-color: ${token.colorFillQuaternary} !important;
          }

          /* Individual cell hover - match row hover */
          .production-enhanced .ant-table-tbody > tr.inactive-row > td:hover {
            background-color: ${token.colorFillQuaternary} !important;
          }

          /* Keep background when dropdown is open */
          .production-enhanced .ant-table-tbody > tr.inactive-row.ant-dropdown-open > td,
          .production-enhanced .ant-table-tbody > tr.inactive-row.ant-dropdown-open > td:hover {
            background-color: ${token.colorBgLayout} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row td {
            color: ${token.colorTextTertiary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row a {
            color: ${token.colorTextTertiary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn,
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn:hover,
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn:focus,
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn:active {
            color: ${token.colorTextTertiary} !important;
            background: transparent !important;
            border-color: transparent !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row .anticon {
            color: ${token.colorTextTertiary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn .anticon {
            color: ${token.colorTextTertiary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-tag {
            opacity: 0.5;
            filter: grayscale(100%);
          }

          /* Gray out category color dots in inactive rows */
          .production-enhanced .ant-table-tbody > tr.inactive-row div[style*="border-radius: 50%"],
          .production-enhanced .ant-table-tbody > tr.inactive-row div[style*="borderRadius"] {
            background-color: ${token.colorTextTertiary} !important;
            opacity: 0.5;
          }
        `}
      </style>

      {/* Vendor Header Card */}
      <VendorHeader
        vendor={vendor}
        canEdit={canEdit}
        toggling={toggling}
        onEditClick={() => setEditHeaderModalOpen(true)}
        onToggleStatus={() => toggleStatusDialog.open(vendor)}
        currentColorScheme={currentColorScheme}
        currentFontSizes={currentFontSizes}
      />

      {/* Main Content with Sidebar */}
      <VendorTabs
        activeSection={activeSection}
        menuItems={menuItems}
        onSectionChange={setActiveSection}
        currentColorScheme={currentColorScheme}
        currentFontSizes={currentFontSizes}
      >
        {renderContent()}
      </VendorTabs>

      {/* Bank Account Modal */}
      <BankAccountFormModal
        visible={bankAccountModalOpen}
        onClose={() => {
          setBankAccountModalOpen(false);
          setEditingBankAccount(null);
          setEditingBankId(null);
        }}
        onSuccess={handleSaveBankAccount}
        bankAccount={editingBankAccount}
        readOnly={bankAccountReadOnly}
      />

      {/* Location Modal */}
      <LocationFormModal
        visible={locationModalOpen}
        onClose={() => {
          setLocationModalOpen(false);
          setEditingLocationId(null);
        }}
        onSuccess={handleSaveLocation}
        location={editingLocationId ? editingLocationFromApi : null}
        loading={editingLocationId ? locationDetailLoading : false}
        readOnly={locationReadOnly}
      />

      {/* Confirmation Dialog for Activate/Deactivate Vendor */}
      <ConfirmationDialog
        open={toggleStatusDialog.isOpen}
        onClose={toggleStatusDialog.close}
        onConfirm={async () => {
          const vendorData = toggleStatusDialog.data;
          if (vendorData && numericVendorId && vendor) {
            setToggling(true);
            try {
              const newStatus = !vendorData.isActive;

              // Call API to toggle status
              await toggleStatus(numericVendorId, newStatus);

              const action = vendorData.isActive ? t('vendor.messages.vendorDeactivated') : t('vendor.messages.vendorActivated');
              messageApi.success(action);

              // Force cache update with fresh data
              // Using refresh() re-runs the request with same params
              refresh();

              toggleStatusDialog.close();
            } catch (error) {
              messageApi.error(t('vendor.messages.statusUpdateFailed'));
              console.error('Error toggling vendor status:', error);
            } finally {
              setToggling(false);
            }
          }
        }}
        title={toggleStatusDialog.data?.isActive ? t('vendor.modals.deactivateVendorTitle') : t('vendor.modals.activateVendorTitle')}
        message={toggleStatusDialog.data?.isActive ? t('vendor.modals.deactivateVendorConfirm') : t('vendor.modals.activateVendorConfirm')}
        type={toggleStatusDialog.data?.isActive ? 'danger' : 'info'}
        confirmText={toggleStatusDialog.data?.isActive ? t('vendor.actions.deactivate') : t('vendor.actions.activate')}
        loading={toggling}
      />

      {/* Confirmation Dialog for Activate/Deactivate Bank Account */}
      <ConfirmationDialog
        open={toggleBankStatusDialog.isOpen}
        onClose={toggleBankStatusDialog.close}
        onConfirm={async () => {
          const bankData = toggleBankStatusDialog.data;
          if (bankData && numericVendorId) {
            // Check if trying to deactivate a default bank account
            if (bankData.isActive && bankData.isDefault) {
              messageApi.error(t('vendor.messages.cannotDeactivateDefaultBankAccount'));
              toggleBankStatusDialog.close();
              return;
            }

            setTogglingBank(true);
            try {
              const newStatus = !bankData.isActive;

              // Call API to toggle bank account status
              await toggleBankStatus(bankData.bankId, newStatus);

              const action = bankData.isActive ? t('vendor.messages.bankAccountDeactivated') : t('vendor.messages.bankAccountActivated');
              messageApi.success(action);

              // Store is updated optimistically by mutations
              toggleBankStatusDialog.close();
            } catch (error) {
              messageApi.error(t('vendor.messages.bankAccountStatusUpdateFailed'));
              console.error('Error toggling bank account status:', error);
            } finally {
              setTogglingBank(false);
            }
          }
        }}
        title={toggleBankStatusDialog.data?.isActive ? t('vendor.modals.deactivateBankAccountTitle') : t('vendor.modals.activateBankAccountTitle')}
        message={toggleBankStatusDialog.data?.isActive ? t('vendor.modals.deactivateBankAccountConfirm') : t('vendor.modals.activateBankAccountConfirm')}
        type={toggleBankStatusDialog.data?.isActive ? 'danger' : 'info'}
        confirmText={toggleBankStatusDialog.data?.isActive ? t('vendor.actions.deactivate') : t('vendor.actions.activate')}
        loading={togglingBank}
      />

      {/* Confirmation Dialog for Activate/Deactivate Location */}
      <ConfirmationDialog
        open={toggleLocationStatusDialog.isOpen}
        onClose={toggleLocationStatusDialog.close}
        onConfirm={async () => {
          const locationData = toggleLocationStatusDialog.data;
          if (locationData && numericVendorId) {
            // Check if trying to deactivate a default location
            if (locationData.isActive && locationData.isDefaultReceiving) {
              messageApi.error(t('vendor.messages.cannotDeactivateDefaultLocation'));
              toggleLocationStatusDialog.close();
              return;
            }

            setTogglingLocation(true);
            try {
              const newStatus = !locationData.isActive;
              await locationMutations.toggleStatus(locationData.locationId, newStatus);
              // Refresh locations list to reflect updated status (cache already invalidated inside mutation)
              await refreshLocations();
              messageApi.success(
                newStatus
                  ? t('vendor.notifications.locationActivated')
                  : t('vendor.notifications.locationDeactivated')
              );
              toggleLocationStatusDialog.close();
            } catch (error) {
              messageApi.error(t('vendor.messages.locationStatusUpdateFailed'));
              console.error('Error toggling location status:', error);
            } finally {
              setTogglingLocation(false);
            }
          }
        }}
        title={toggleLocationStatusDialog.data?.isActive ? t('vendor.modals.deactivateLocationTitle') : t('vendor.modals.activateLocationTitle')}
        message={toggleLocationStatusDialog.data?.isActive ? t('vendor.modals.deactivateLocationConfirm') : t('vendor.modals.activateLocationConfirm')}
        type={toggleLocationStatusDialog.data?.isActive ? 'danger' : 'info'}
        confirmText={toggleLocationStatusDialog.data?.isActive ? t('vendor.actions.deactivate') : t('vendor.actions.activate')}
        loading={togglingLocation}
      />

      {/* Confirmation Dialog for Set Default Location */}
      <ConfirmationDialog
        open={setDefaultLocationDialog.isOpen}
        onClose={setDefaultLocationDialog.close}
        onConfirm={async () => {
          const locationData = setDefaultLocationDialog.data;
          if (locationData && numericVendorId) {
            setSettingDefaultLocation(true);
            try {
              // Find the current default location name for success message
              const currentDefault = locations?.find(l => l.isDefaultReceiving);
              const previousDefaultName = currentDefault?.locationName;

              await locationMutations.setDefault(locationData.locationId, true);
              // Refresh locations list to reflect updated default (cache already invalidated inside mutation)
              await refreshLocations();
              messageApi.success(
                previousDefaultName
                  ? t('vendor.messages.locationSetAsDefaultWithPrevious', {
                      previous: previousDefaultName
                    })
                  : t('vendor.notifications.defaultLocationSet')
              );
              setDefaultLocationDialog.close();
            } catch (error) {
              messageApi.error(t('vendor.messages.locationSetDefaultFailed'));
              console.error('Error setting default location:', error);
            } finally {
              setSettingDefaultLocation(false);
            }
          }
        }}
        title={t('vendor.modals.setDefaultLocationTitle')}
        message={
          (() => {
            const currentDefault = locations?.find(l => l.isDefaultReceiving);
            const previousDefaultName = currentDefault?.locationName;
            return previousDefaultName
              ? t('vendor.modals.setDefaultLocationConfirmWithPrevious', {
                  previous: previousDefaultName
                })
              : t('vendor.modals.setDefaultLocationConfirm');
          })()
        }
        type="info"
        confirmText={t('vendor.actions.setAsDefault')}
        loading={settingDefaultLocation}
      />

      {/* Confirmation Dialog for Set Default Bank */}
      <ConfirmationDialog
        open={setDefaultBankDialog.isOpen}
        onClose={setDefaultBankDialog.close}
        onConfirm={async () => {
          const bankData = setDefaultBankDialog.data;
          if (bankData && numericVendorId) {
            setSettingDefaultBank(true);
            try {
              await setPrimaryBank(bankData.bankId, true);
              messageApi.success(t('vendor.messages.bankAccountSetAsDefault'));
              setDefaultBankDialog.close();
            } catch (error: unknown) {
              const errorMessage =
                (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
                t('vendor.messages.statusUpdateFailed');
              messageApi.error(errorMessage);
              console.error('Set default bank account error:', error);
            } finally {
              setSettingDefaultBank(false);
            }
          }
        }}
        title={t('vendor.modals.defaultBankAccountExists')}
        message={
          (() => {
            const currentDefault = bankAccounts?.find((b) => b.isDefault);
            const bankData = setDefaultBankDialog.data;
            return currentDefault && bankData && currentDefault.bankId !== bankData.bankId
              ? t('vendor.modals.replaceDefaultBankAccount', {
                  current: currentDefault.accountName,
                  new: bankData.accountName,
                })
              : t('vendor.modals.setDefaultBankAccountConfirm');
          })()
        }
        type="info"
        confirmText={t('vendor.modals.replaceDefault')}
        loading={settingDefaultBank}
      />

      {/* Edit Vendor Header Modal */}
      <EditVendorHeaderModal
        open={editHeaderModalOpen}
        onCancel={() => setEditHeaderModalOpen(false)}
        onSave={handleSaveHeader}
        vendor={vendor}
        categories={categories}
        loadingCategories={categoriesLoading}
        onCategoryCreated={fetchCategories}
      />

      {/* Edit Basic Information Modal */}
      <EditBasicInfoModal
        open={editBasicInfoModalOpen}
        onCancel={() => setEditBasicInfoModalOpen(false)}
        onSave={handleSaveBasicInfo}
        vendor={vendor}
      />

      {/* Edit Financial Information Modal */}
      <EditFinancialInfoModal
        open={editFinancialInfoModalOpen}
        onCancel={() => setEditFinancialInfoModalOpen(false)}
        onSave={handleSaveFinancialInfo}
        payment={payment}
        countryId={vendor?.countryId}
        localTaxId={vendor?.localTaxId}
      />
    </PageContainer>
  );
};

export default VendorDetailPage;
